import { DialogCloseEvent } from '@theme/dialog';
import { clamp, preventDefault } from '@theme/utilities';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
/** AP-EDIT 2026-09-16 (user): mở dialog ở 1x để thấy cả ảnh (Horizon gốc mở 1.5x) */
const OPEN_ZOOM = MIN_ZOOM;
const DOUBLE_TAP_ZOOM = 2;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;
const TAP_MAX_DURATION = 250;
const SWIPE_MIN_DISTANCE = 50;
/** Desktop zoom lên cỡ thật của ảnh, nhưng không dưới 2x / quá 4x */
const DESKTOP_MIN_ZOOM_IN = 2;
const DESKTOP_MAX_ZOOM_IN = 4;

/**
 * AP-EDIT 2026-09-16: chọn chế độ theo kiểu thiết bị chứ không chỉ theo bề ngang.
 * Horizon gốc chỉ bật cử chỉ dưới 750px, nên iPad rơi vào chế độ desktop (không pinch,
 * chạm là đóng). Query này PHẢI khớp media query trong product-media-gallery-content.liquid.
 */
const touchModeQuery = matchMedia('(max-width: 749px), (hover: none) and (pointer: coarse)');
const isTouchMode = () => touchModeQuery.matches;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/**
 * Zoom ảnh trong dialog.
 * - Touch (mobile/tablet): pinch, kéo, chạm đôi zoom, chạm đơn về cỡ lúc mở, vuốt ngang ở 1x để đổi ảnh.
 * - Desktop: click vào ảnh zoom lên cỡ thật tại điểm click, rê chuột để xem vùng khác, click lần nữa thu về.
 *   Click ra nền ngoài ảnh (khi chưa zoom) thì dialog đóng như cũ.
 *
 * @extends {Component<Refs>}
 */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = OPEN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = OPEN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;
  /** AP-EDIT 2026-09-11 (user): chạm 1 ngón đưa ảnh về đúng cỡ lúc mở @type {number | null} */
  #singleTapTimer = null;
  /** @type {boolean} */
  #gestureUsedTwoFingers = false;
  /** @type {number} */
  #touchStartTime = 0;
  /** Cú nhấc tay khép lại một chạm đôi thì không được hẹn chạm đơn @type {boolean} */
  #suppressNextSingleTap = false;
  /** Cử chỉ bắt đầu ở 1x thì mới được tính là vuốt đổi ảnh @type {boolean} */
  #gestureStartedAtMinZoom = true;

  /** @type {boolean} */
  #hasManualZoom = false;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#resizeObserver.observe(this);
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    touchModeQuery.addEventListener('change', this.#resetZoom, { signal: this.#controller.signal });

    this.#initEventListeners();
    this.#updateTransform();
  }

  #initEventListeners() {
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    // Touch events chỉ bắn trên thiết bị cảm ứng; mỗi handler tự bỏ qua khi đang ở chế độ desktop
    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('mousemove', this.#handleMouseMove, { signal });
    this.addEventListener('wheel', this.#handleWheel, { passive: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    this.#requestUpdateTransform();
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /* ------------------------------------------------------------------ */
  /* Desktop                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (isTouchMode()) return;

    const isZoomed = this.#scale > MIN_ZOOM;

    // Chưa zoom mà click ra nền ngoài ảnh -> đóng dialog.
    // Không dùng on:click="/close" trên <li> được: Component bắt on:click ở pha capture của document,
    // chạy TRƯỚC listener này nên mọi click vào ảnh đều đóng dialog.
    if (!isZoomed && !this.#isPointOverImage(event)) {
      const dialog = /** @type {(HTMLElement & { close?: Function }) | null} */ (this.closest('zoom-dialog'));
      dialog?.close?.();
      return;
    }

    if (isZoomed) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
    } else {
      this.#scale = this.#getDesktopZoomLevel();
      this.#panToPointer(event);
    }

    this.#syncDesktopCursor(event);
    this.#requestUpdateTransform();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (isTouchMode()) return;

    if (this.#scale > MIN_ZOOM) {
      this.#panToPointer(event);
      this.#requestUpdateTransform();
    }

    this.#syncDesktopCursor(event);
  };

  /** Lăn chuột sang ảnh khác thì trả ảnh đang zoom về 1x */
  #handleWheel = () => {
    if (isTouchMode() || this.#scale <= MIN_ZOOM) return;
    this.#resetZoom();
  };

  /**
   * Zoom tới cỡ thật của ảnh (mức nét nhất có thể), kẹp trong [2x, 4x]
   * @returns {number}
   */
  #getDesktopZoomLevel() {
    const fitted = this.#getFittedImageSize(this.getBoundingClientRect());
    const naturalWidth = this.#image?.naturalWidth || 0;
    const nativeZoom = fitted.width > 0 && naturalWidth > 0 ? naturalWidth / fitted.width : DESKTOP_MIN_ZOOM_IN;
    return clamp(nativeZoom, DESKTOP_MIN_ZOOM_IN, DESKTOP_MAX_ZOOM_IN);
  }

  /**
   * Con trỏ ở mép nào của khung thì hiện mép đó của ảnh
   * @param {MouseEvent} event
   */
  #panToPointer(event) {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const fitted = this.#getFittedImageSize(rect);
    const overflowX = Math.max(0, fitted.width * this.#scale - rect.width);
    const overflowY = Math.max(0, fitted.height * this.#scale - rect.height);
    const fx = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const fy = clamp((event.clientY - rect.top) / rect.height, 0, 1);

    // translate() đứng sau scale() nên tính theo đơn vị trước khi scale
    this.#translate.x = ((0.5 - fx) * overflowX) / this.#scale;
    this.#translate.y = ((0.5 - fy) * overflowY) / this.#scale;
  }

  /**
   * Con trỏ có nằm trên phần ảnh thật (không phải dải nền do object-fit: contain) không
   * @param {MouseEvent} event
   */
  #isPointOverImage(event) {
    const rect = this.getBoundingClientRect();
    const fitted = this.#getFittedImageSize(rect);
    const left = rect.left + (rect.width - fitted.width) / 2;
    const top = rect.top + (rect.height - fitted.height) / 2;
    return (
      event.clientX >= left &&
      event.clientX <= left + fitted.width &&
      event.clientY >= top &&
      event.clientY <= top + fitted.height
    );
  }

  /**
   * @param {MouseEvent} event
   */
  #syncDesktopCursor(event) {
    const isZoomed = this.#scale > MIN_ZOOM;
    this.classList.toggle('is-zoomed', isZoomed);
    this.classList.toggle('is-over-image', !isZoomed && this.#isPointOverImage(event));
  }

  /* ------------------------------------------------------------------ */
  /* Touch                                                               */
  /* ------------------------------------------------------------------ */

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (!isTouchMode()) return;
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      this.#gestureUsedTwoFingers = true;
      this.#cancelSingleTap();
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      this.#cancelSingleTap();
      this.#gestureUsedTwoFingers = false;
      this.#gestureStartedAtMinZoom = this.#scale <= MIN_ZOOM + 0.01;
      this.#touchStartTime = performance.now();

      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Early exit if too much time has passed
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      // Only check distance if we have a previous tap within time window
      if (this.#lastTapPosition) {
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          // This is a double-tap, handle zoom toggle
          this.#cancelSingleTap();
          this.#suppressNextSingleTap = true;
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0; // Reset to prevent triple-tap
          this.#lastTapPosition = null;
          return;
        }
      }

      // Store tap info for potential double-tap detection
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle double-tap zoom toggle from touch
   * @param {Touch} touch - The touch where the double-tap occurred
   */
  #handleDoubleTapFromTouch(touch) {
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    let targetZoom;

    // If manual zoom has been used, reset to 1x
    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM;
      this.#hasManualZoom = false;
      this.#translate = { x: 0, y: 0 };
    } else {
      // Toggle between zoom levels: 1x ↔ DOUBLE_TAP_ZOOM
      const tolerance = 0.05;
      targetZoom = Math.abs(this.#scale - MIN_ZOOM) < tolerance ? DOUBLE_TAP_ZOOM : MIN_ZOOM;
    }

    // If we're not going to 1x, adjust translation to center zoom on the tap point
    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = Math.min(MAX_ZOOM, targetZoom);

      const distanceFromCenter = {
        x: touch.clientX - containerCenter.x,
        y: touch.clientY - containerCenter.y,
      };

      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    } else {
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 };
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (!isTouchMode()) return;
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    const currentDistance = getDistance(touch1, touch2);
    const oldScale = this.#scale;

    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    this.#hasManualZoom = true;

    // Adjust translation to keep the pinch midpoint stationary
    const distanceFromCenterX = midX - this.clientWidth / 2;
    const distanceFromCenterY = midY - this.clientHeight / 2;
    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (!isTouchMode()) return;
    if (event.touches.length !== 0) return;

    const endTouch = event.changedTouches[0];
    const wasCleanTap =
      !this.#suppressNextSingleTap &&
      !this.#gestureUsedTwoFingers &&
      !this.#hasDraggedBeyondThreshold &&
      performance.now() - this.#touchStartTime < TAP_MAX_DURATION;
    const wasSingleFingerDrag = this.#hasDraggedBeyondThreshold && !this.#gestureUsedTwoFingers;

    this.#isDragging = false;
    this.#requestUpdateTransform();

    this.#hasDraggedBeyondThreshold = false;
    this.#suppressNextSingleTap = false;

    // AP-EDIT 2026-09-16 (user): ở 1x thì vuốt ngang để đổi ảnh (Horizon gốc chặn hết, chỉ đổi được bằng thumbnail)
    if (wasSingleFingerDrag && this.#gestureStartedAtMinZoom && endTouch) {
      const dx = endTouch.clientX - this.#startPosition.x;
      const dy = endTouch.clientY - this.#startPosition.y;
      if (Math.abs(dx) >= SWIPE_MIN_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
        this.#goToSiblingMedia(dx < 0 ? 1 : -1);
        return;
      }
      // AP-EDIT 2026-09-17 (user): ở 1x vuốt DỌC (lên hoặc xuống) là đóng fullscreen, không cần bấm X.
      if (Math.abs(dy) >= SWIPE_MIN_DISTANCE && Math.abs(dy) > Math.abs(dx)) {
        const dialog = /** @type {(HTMLElement & { close?: Function }) | null} */ (this.closest('zoom-dialog'));
        if (typeof dialog?.close === 'function') {
          dialog.close();
          return;
        }
      }
    }

    // AP-EDIT 2026-09-11 (user): chạm 1 cái đưa ảnh về đúng cỡ lúc vừa mở.
    // Hẹn qua DOUBLE_TAP_DELAY để cú chạm thứ hai còn kịp giành quyền
    // (chạm đôi vẫn về 1x như cũ).
    if (wasCleanTap && this.#isAwayFromOpenState()) {
      this.#singleTapTimer = window.setTimeout(() => {
        this.#singleTapTimer = null;
        this.#hasManualZoom = false;
        this.#resetZoom();
      }, DOUBLE_TAP_DELAY);
    }
  };

  /**
   * Chuyển sang ảnh trước/sau qua zoom-dialog để thumbnail đồng bộ theo
   * @param {number} step - 1 = ảnh sau, -1 = ảnh trước
   */
  #goToSiblingMedia(step) {
    const item = this.closest('li');
    const dialog = /** @type {(HTMLElement & { selectThumbnail?: Function }) | null} */ (this.closest('zoom-dialog'));
    if (!item?.parentElement || typeof dialog?.selectThumbnail !== 'function') return;

    const items = Array.from(item.parentElement.children);
    const index = items.indexOf(item) + step;
    if (index < 0 || index >= items.length) return;

    dialog.selectThumbnail(index);
  }

  /** Huỷ cú chạm đơn đang chờ */
  #cancelSingleTap() {
    if (this.#singleTapTimer == null) return;
    clearTimeout(this.#singleTapTimer);
    this.#singleTapTimer = null;
  }

  /** Ảnh có đang lệch khỏi trạng thái lúc mở không */
  #isAwayFromOpenState() {
    return (
      Math.abs(this.#scale - OPEN_ZOOM) > 0.01 ||
      Math.abs(this.#translate.x) > 0.5 ||
      Math.abs(this.#translate.y) > 0.5
    );
  }

  /* ------------------------------------------------------------------ */
  /* Shared                                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Kích thước phần ảnh thật ở 1x bên trong khung (object-fit: contain)
   * @param {DOMRect} wrapperRect
   * @returns {{ width: number, height: number }}
   */
  #getFittedImageSize(wrapperRect) {
    const image = this.#image;
    const naturalWidth = image?.naturalWidth || wrapperRect.width;
    // Chưa load được ảnh thì giả định ảnh vuông
    const naturalHeight = image?.naturalHeight || wrapperRect.width;
    if (!naturalWidth || !naturalHeight || !wrapperRect.height) return { width: 0, height: 0 };

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider - width fits exactly (letterboxed top/bottom)
      return { width: wrapperRect.width, height: wrapperRect.width / imageAspectRatio };
    }
    // Image is taller - height fits exactly (letterboxed left/right)
    return { width: wrapperRect.height * imageAspectRatio, height: wrapperRect.height };
  }

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    if (!this.clientWidth || !this.clientHeight || !this.#image) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      // AP-EDIT 2026-09-17: đang kéo dọc ở 1x thì cho ảnh đi theo ngón tay (báo hiệu "vuốt là đóng");
      // thả tay chưa đủ SWIPE_MIN_DISTANCE thì #handleTouchEnd tắt isDragging → về 0 như cũ.
      if (!(this.#isDragging && this.#gestureStartedAtMinZoom)) this.#translate.y = 0;
      return;
    }

    const wrapperRect = this.getBoundingClientRect();
    const fitted = this.#getFittedImageSize(wrapperRect);

    const horizontalOverflow = Math.max(0, fitted.width * this.#scale - wrapperRect.width);
    const verticalOverflow = Math.max(0, fitted.height * this.#scale - wrapperRect.height);

    // Max translation is half the overflow (since image starts centered)
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.classList.toggle('is-zoomed', this.#scale > MIN_ZOOM);
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  /**
   * Reset zoom to the open state (OPEN_ZOOM, centered)
   * Called when zoom is exited/closed
   */
  #resetZoom = () => {
    this.#cancelSingleTap();

    this.#scale = OPEN_ZOOM;
    this.#startScale = OPEN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#suppressNextSingleTap = false;
    this.#hasManualZoom = false;

    this.classList.remove('is-zoomed', 'is-over-image');
    this.style.setProperty('--drag-zoom-scale', OPEN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  destroy() {
    this.#cancelSingleTap();
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */
